import { useEffect, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
  alpha
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import SyncIcon from '@mui/icons-material/Sync'

import {
  isBitbucketRefreshClientDisconnect,
  listSocrRepositories,
  normalizeRepoUuid,
  refreshSocrRepositoryBitbucket,
  refreshSocrRepositoryDt,
  resolveSocrRepository
} from '../../api/socrInventory'
import { Section } from './Section'
import { RepoDtBuildDialog } from './RepoDtPanel'

const repoLabel = (repo) =>
  repo?.repositoryName || repo?.repositorySlug || repo?.repositoryUuid || 'repo'

const ClientBitbucketSection = ({
  token,
  repos = [],
  saving = false,
  codeTreeReposByUuid = {},
  onLinkRepos,
  onUnlink,
  onInventoryChanged,
  onFlash
}) => {
  const [urlDraft, setUrlDraft] = useState('')
  const [resolving, setResolving] = useState(false)
  const [resolved, setResolved] = useState(null)
  const [resolveError, setResolveError] = useState(null)

  const [repoOptions, setRepoOptions] = useState([])
  const [repoQuery, setRepoQuery] = useState('')
  const [repoPick, setRepoPick] = useState(null)
  const [loadingRepos, setLoadingRepos] = useState(false)

  const [buildTarget, setBuildTarget] = useState(null)
  const [syncingUuid, setSyncingUuid] = useState(null)
  const [syncingBitbucketUuid, setSyncingBitbucketUuid] = useState(null)

  useEffect(() => {
    if (!token) return undefined
    let cancelled = false
    const handle = window.setTimeout(async () => {
      setLoadingRepos(true)
      try {
        const rows = await listSocrRepositories(token, { q: repoQuery.trim() || undefined })
        if (!cancelled) setRepoOptions(Array.isArray(rows) ? rows : [])
      } catch {
        if (!cancelled) setRepoOptions([])
      } finally {
        if (!cancelled) setLoadingRepos(false)
      }
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
  }, [token, repoQuery])

  const syncRepoDt = async (uuid) => {
    if (!token || !uuid) return
    setSyncingUuid(uuid)
    try {
      const result = await refreshSocrRepositoryDt(token, uuid)
      const added = result?.added ?? 0
      const removed = result?.removed ?? 0
      const renamed = result?.renamed ?? 0
      onInventoryChanged?.()
      onFlash?.(
        'success',
        `DT sync for repo: +${added} / −${removed} / renamed ${renamed}`
      )
    } catch (err) {
      onFlash?.('error', err.message || 'DT sync failed for repository')
    } finally {
      setSyncingUuid(null)
    }
  }

  const syncRepoBitbucket = async (uuid) => {
    if (!token || !uuid) return
    setSyncingBitbucketUuid(uuid)
    try {
      const result = await refreshSocrRepositoryBitbucket(token, uuid, {
        includeRuntimes: true
      })
      const upserted = result?.runtimesUpserted ?? 0
      const removed = result?.runtimesRemoved ?? 0
      onInventoryChanged?.()
      onFlash?.(
        'success',
        `Bitbucket sync (+ runtimes): +${upserted}/−${removed}`
      )
    } catch (err) {
      // Server keeps scanning after client/proxy disconnect — refresh inventory instead of failing hard.
      if (isBitbucketRefreshClientDisconnect(err)) {
        onInventoryChanged?.()
        onFlash?.(
          'success',
          'Bitbucket runtime scan continues server-side — inventory reloaded; check again shortly if TFMs look stale'
        )
      } else {
        onFlash?.('error', err.message || 'Bitbucket sync failed for repository')
      }
    } finally {
      setSyncingBitbucketUuid(null)
    }
  }

  const handleResolve = async () => {
    const url = urlDraft.trim()
    if (!url || !token) return
    setResolving(true)
    setResolveError(null)
    setResolved(null)
    try {
      const row = await resolveSocrRepository(token, { url })
      setResolved(row)
      onInventoryChanged?.()
    } catch (err) {
      setResolveError(err.message || 'Resolve failed')
    } finally {
      setResolving(false)
    }
  }

  const linkResolved = async () => {
    if (!resolved) return
    await onLinkRepos?.({
      repositoryUuid: normalizeRepoUuid(resolved.repositoryUuid),
      repositoryName: resolved.repositoryName || resolved.repositorySlug,
      repositorySlug: resolved.repositorySlug || ''
    })
    setResolved(null)
    setUrlDraft('')
  }

  const linkInventory = async () => {
    if (!repoPick) return
    await onLinkRepos?.({
      repositoryUuid: normalizeRepoUuid(repoPick.repositoryUuid),
      repositoryName: repoPick.repositoryName || repoPick.repositorySlug,
      repositorySlug: repoPick.repositorySlug || ''
    })
    setRepoPick(null)
  }

  return (
    <Section
      title='Bitbucket + DT'
      hint='Link repos by URL or inventory. Sync runtimes starts a server-side .csproj TFM rescan (client does not hold a multi-minute HTTP socket). Sync DT refreshes Dependency-Track project names; Run DT scan opens the branch/pipeline modal.'
    >
      <Stack spacing={1.5}>
        {(repos || []).length
          ? repos.map((repo) => {
            const uuid = normalizeRepoUuid(repo.repositoryUuid)
            const treeRepo = codeTreeReposByUuid[uuid]
            const dtCount = Array.isArray(treeRepo?.dtProjects)
              ? treeRepo.dtProjects.length
              : (Array.isArray(repo.dtProjects) ? repo.dtProjects.length : null)
            const label = repoLabel(repo)

            return (
              <Box
                key={uuid || repo.repositorySlug}
                sx={{
                  borderRadius: 1.5,
                  border: `1px solid ${alpha('#fff', 0.1)}`,
                  px: 1.25,
                  py: 0.85,
                  backgroundColor: alpha('#fff', 0.03)
                }}
              >
                <Stack direction='row' alignItems='center' spacing={1}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.86rem' }} noWrap>
                      {label}
                    </Typography>
                    <Typography variant='caption' sx={{ color: alpha('#fff', 0.65), fontWeight: 600 }}>
                      {repo.repositorySlug || uuid}
                      {dtCount != null ? ` · ${dtCount} DT project${dtCount === 1 ? '' : 's'}` : ''}
                    </Typography>
                  </Box>
                  <Button
                    size='small'
                    variant='text'
                    startIcon={<SyncIcon />}
                    disabled={!uuid || syncingBitbucketUuid === uuid || syncingUuid === uuid}
                    onClick={() => syncRepoBitbucket(uuid)}
                    title='Rescan default-branch .NET TFMs from Bitbucket (.csproj; can take several minutes)'
                  >
                    {syncingBitbucketUuid === uuid ? 'Syncing…' : 'Sync runtimes'}
                  </Button>
                  <Button
                    size='small'
                    variant='text'
                    startIcon={<SyncIcon />}
                    disabled={!uuid || syncingUuid === uuid || syncingBitbucketUuid === uuid}
                    onClick={() => syncRepoDt(uuid)}
                    title='Refresh DT project names for this repository only'
                  >
                    {syncingUuid === uuid ? 'Syncing…' : 'Sync DT'}
                  </Button>
                  <Button
                    size='small'
                    variant='outlined'
                    startIcon={<PlayArrowIcon />}
                    disabled={!uuid}
                    onClick={() => setBuildTarget({
                      uuid,
                      label,
                      defaultBranch:
                        resolved?.repositoryUuid &&
                        normalizeRepoUuid(resolved.repositoryUuid) === uuid
                          ? (resolved.defaultBranch || '')
                          : (treeRepo?.defaultBranch || '')
                    })}
                  >
                    Run DT scan
                  </Button>
                  <IconButton
                    size='small'
                    disabled={saving}
                    onClick={() => onUnlink?.(uuid)}
                    title='Unlink repository'
                  >
                    <DeleteOutlineIcon fontSize='small' />
                  </IconButton>
                </Stack>
              </Box>
            )
          })
          : (
            <Typography variant='caption' sx={{ color: alpha('#fff', 0.5) }}>
              No repositories linked
            </Typography>
            )}

        <Stack spacing={1}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Add by Bitbucket URL</Typography>
          {resolveError ? <Alert severity='error'>{resolveError}</Alert> : null}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              size='small'
              fullWidth
              label='Repository URL'
              placeholder='https://bitbucket.org/workspace/repo'
              value={urlDraft}
              disabled={resolving || saving}
              onChange={(e) => {
                setUrlDraft(e.target.value)
                setResolved(null)
                setResolveError(null)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleResolve()
                }
              }}
            />
            <Button
              size='small'
              variant='outlined'
              disabled={!urlDraft.trim() || resolving || saving}
              onClick={handleResolve}
            >
              {resolving ? 'Resolving…' : 'Resolve'}
            </Button>
          </Stack>
          {resolved
            ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <Chip
                  size='small'
                  color='info'
                  label={`${resolved.repositoryName || resolved.repositorySlug}${resolved.workspace ? ` · ${resolved.workspace}` : ''}${resolved.defaultBranch ? ` · ${resolved.defaultBranch}` : ''}`}
                />
                <Button size='small' variant='contained' disabled={saving} onClick={linkResolved}>
                  Link to client
                </Button>
              </Stack>
              )
            : null}
        </Stack>

        <Stack spacing={1}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Or pick from inventory</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Autocomplete
              size='small'
              fullWidth
              options={repoOptions}
              loading={loadingRepos}
              value={repoPick}
              onChange={(_, value) => setRepoPick(value)}
              onInputChange={(_, value) => setRepoQuery(value)}
              getOptionLabel={(opt) => (
                opt?.repositoryName
                  ? `${opt.repositoryName}${opt.repositorySlug ? ` (${opt.repositorySlug})` : ''}`
                  : ''
              )}
              isOptionEqualToValue={(a, b) =>
                normalizeRepoUuid(a?.repositoryUuid) === normalizeRepoUuid(b?.repositoryUuid)}
              renderInput={(params) => (
                <TextField {...params} label='Inventory repository' placeholder='Search repos' />
              )}
            />
            <Button
              size='small'
              variant='contained'
              disabled={!repoPick || saving}
              onClick={linkInventory}
            >
              Link
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <RepoDtBuildDialog
        open={Boolean(buildTarget?.uuid)}
        onClose={() => setBuildTarget(null)}
        token={token}
        repositoryUuid={buildTarget?.uuid || ''}
        repositoryLabel={buildTarget?.label || ''}
        defaultBranch={buildTarget?.defaultBranch || ''}
        onInventoryChanged={onInventoryChanged}
        onFlash={onFlash}
      />
    </Section>
  )
}

export { ClientBitbucketSection }
